import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  getInternetAmenityPlanIds,
  getFirstInternetAmenityPlan,
} from 'common/internetAmenityPlans';
import determineArticle from 'common/util/determineArticle';

import {
  UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID,
  UTILITY_TYPE_ID_INTERNET,
} from 'common/utility/constants';

import { ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_PLAN_SELECTED } from 'client/analytics/eventList';
import analytics from 'client/analytics';
import { hasRoleAnonymous } from 'common/utility/userRoles';
import useBranch from 'client/hooks/useBranch';

import { makeUtilityActivationContext } from 'client/analytics/makeApplicationContext';

import ButtonDeprecated, { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrUtilitySetupTransferedAccountModal from 'client/components/SrUtilitySetup/SrUtilitySetupTransferedAccountModal';
import SrUtilitySetupNonContractPlansModal from 'client/components/SrUtilitySetup/SrUtilitySetupNonContractPlansModal';
import SrUtilityProfitProviderPlanCard, {
  PROVIDER_PLAN_CARD_THEME_AMENITY,
  PROVIDER_PLAN_CARD_THEME_DEFAULT,
  PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY,
} from 'client/components/SrUtilityProfitProviderPlanCard/SrUtilityProfitProviderPlanCard';
import UPResidualUtilityFaqModal from 'client/components/UPResidualUtilityFaqModal/UPResidualUtilityFaqModal';
import SrUtilitySetupTransferInternetFlow from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlow';
import SrUtilitySetupTransferInternetButton from 'client/components/SrUtilitySetup/SrUtilitySetupTransferInternetButton';

import './SrUtilitySetupProviderPlan.less';

const makeIsSelectedPlanFn = ({
  utilityProviderPlanId,
  utilityProviderPlanVariantId,
  internetAmenityPlanId,
}) => {
  return (plan) => {
    const planMatches = plan.id === utilityProviderPlanId;
    const planVariantMatches =
      (!plan.utilityProviderPlanVariantId && !utilityProviderPlanVariantId) ||
      plan.utilityProviderPlanVariantId === utilityProviderPlanVariantId;
    const amenityPlanMatches =
      (!internetAmenityPlanId && !plan.internetAmenityPlanId) ||
      plan.internetAmenityPlanId === internetAmenityPlanId;

    return planMatches && planVariantMatches && amenityPlanMatches;
  };
};

const getPageTitle = ({ isNoContractPlans, utilityActivationItem, isInternetAmenityFlow }) => {
  if (isInternetAmenityFlow) {
    return <>Select an internet plan:</>;
  }

  return isNoContractPlans
    ? `No contract ${utilityActivationItem.type.label.toLowerCase()}`
    : `Select ${determineArticle(utilityActivationItem.type.label.toLowerCase())} plan:`;
};

const SrUtilitySetupProviderPlan = ({
  utilityPartner,
  utilityActivation,
  utilityActivationItem,
  utilityProviders,
  history,
  locationQuery,
  transferFlowIsActive,
  isNoContractPlans,
  onCloseTransferFlow,
  onRefreshUtilityActivation,
  onStartTransferInternetFlow,
  internetPlanDescriptionV2,
}) => {
  const defaultInternetAmenityPlan = getFirstInternetAmenityPlan(utilityProviders);
  const initialDefaultInternetAmenityPlan = defaultInternetAmenityPlan;
  const internetAmenityPlanIds = getInternetAmenityPlanIds(utilityProviders);
  const defaultInternetAmenityPlanProvider = utilityProviders.find(({ plans = [] }) =>
    plans.find((plan) => {
      return plan.internetAmenityPlanId === defaultInternetAmenityPlan?.internetAmenityPlanId;
    }),
  );
  const isInternetAmenityFlow =
    defaultInternetAmenityPlan &&
    defaultInternetAmenityPlanProvider?.purchaseFlowType !==
      UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID;

  const [utilityProviderId, setUtilityProviderId] = useState(
    utilityActivationItem.utilityProviderId ||
      initialDefaultInternetAmenityPlan?.utilityProviderId ||
      (locationQuery.providerId ? parseInt(locationQuery.providerId, 10) : null),
  );
  const [utilityProviderPlanId, setUtilityProviderPlanId] = useState(
    utilityActivationItem.utilityProviderPlanId ||
      initialDefaultInternetAmenityPlan?.utilityProviderPlanId ||
      (locationQuery.id ? parseInt(locationQuery.id, 10) : null),
  );
  const [utilityProviderPlanVariantId, setUtilityProviderPlanVariantId] = useState(
    utilityActivationItem.utilityProviderPlanVariantId ||
      initialDefaultInternetAmenityPlan?.utilityProviderPlanVariantId ||
      (locationQuery.variantId ? parseInt(locationQuery.variantId, 10) : null),
  );
  const [internetAmenityPlanId, setInternetAmenityPlanId] = useState(
    utilityActivationItem.internetAmenityPlanId ||
      initialDefaultInternetAmenityPlan?.internetAmenityPlanId ||
      null,
  );

  const [showTransferedAccountModal, setShowTransferedAccountModal] = useState(false);
  const [noContractPlanModalProps, setNoContractPlanModalProps] = useState(null);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);

  const { currentUtilityUser } = useBranch({
    currentUtilityUser: ['utilityUser'],
  });
  const isSelectedPlan = makeIsSelectedPlanFn({
    utilityProviderPlanId,
    utilityProviderPlanVariantId,
    internetAmenityPlanId,
  });

  const isInternetUtilityType = utilityActivationItem.type.id === UTILITY_TYPE_ID_INTERNET;
  let availableProviders = utilityProviders;

  if (isNoContractPlans) {
    availableProviders = availableProviders.reduce((memo, provider) => {
      const noContractPlans = provider.plans.filter(
        ({ contractTermMonths }) => !contractTermMonths,
      );
      if (noContractPlans.length > 0) {
        memo.push({
          ...provider,
          plans: noContractPlans,
        });
      }
      return memo;
    }, []);
  }

  const getPlanDescription = (plan) => {
    return internetPlanDescriptionV2 === 'updated' && plan.descriptionVariant
      ? plan.descriptionVariant
      : plan.description;
  };

  const onCheckNonContractPlans = () => {
    const noContractPlanProviders = utilityProviders
      .filter(({ id }) => id !== utilityProviderId)
      .filter(({ plans }) => plans.some(({ contractTermMonths }) => !contractTermMonths));

    const utilityProvider = utilityProviders.find((provider) => provider.id === utilityProviderId);

    const utilityProviderPlan = utilityProvider.plans.find(isSelectedPlan);

    if (utilityProviderPlan.contractTermMonths && noContractPlanProviders.length > 0) {
      setNoContractPlanModalProps({
        selectedProvider: utilityProvider,
        noContractPlanProviders,
      });
    } else {
      onContinue();
    }
  };

  const onContinue = () => {
    const linkParts = [
      '/utility-setup',
      utilityPartner.slug,
      utilityActivation.shortId,
      utilityActivationItem.type.label.toLowerCase(),
      'plan',
      utilityProviderId,
      utilityProviderPlanId,
    ];

    if (utilityProviderPlanVariantId) {
      linkParts.push(utilityProviderPlanVariantId);
    }

    let link = linkParts.join('/');

    if (isNoContractPlans) {
      link += '?isContractToNoContract=true';
    }

    const utilityProvider = utilityProviders.find((provider) => provider.id === utilityProviderId);

    const utilityProviderPlan = utilityProvider.plans.find(isSelectedPlan);

    const importantVerbiageRequired =
      utilityPartner.experienceConfiguration?.importantVerbiageRequired ?? true;

    const isAnonymous = hasRoleAnonymous(currentUtilityUser?.roles || []);

    if (!isAnonymous) {
      analytics.track(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_PLAN_SELECTED, {
        ...makeUtilityActivationContext(utilityActivation),
        utilityPlanTitle: utilityProviderPlan.title,
        utilityPlanInternetSpeedMbps: utilityProviderPlan.internetSpeedMbps,
        utilityPlanDescription: getPlanDescription(utilityProviderPlan),
        utilityPlanPrice: utilityProviderPlan.price,
        utilityPlanIsVariant: !!utilityProviderPlan.utilityProviderPlanVariantId,
        utilityPlanVariantId: utilityProviderPlan.utilityProviderPlanVariantId || null,
        utilityPlanOriginalPrice: utilityProviderPlan.originalPrice,
        utilityPlanPriceUnit: utilityProviderPlan.priceUnit,
        utilityPlanPromotion: utilityProviderPlan.promotion,
        utilityProviderTitle: utilityProvider.title,
        utilityProviderMarketingDescription: utilityProvider.marketingDescription,
        utilityProviderCcRequired: utilityProvider.ccRequired,
        importantVerbiageRequired,
      });
    }

    history.push(link);
  };

  const onSeeNoContractPlanOptions = () => {
    const link = [
      '/utility-setup',
      utilityPartner.slug,
      utilityActivation.shortId,
      utilityActivationItem.type.label.toLowerCase(),
      'plan-no-contract',
    ].join('/');

    history.push(link);
  };

  return (
    <div styleName="view">
      <div styleName="titleBox">
        <span>
          {getPageTitle({
            isNoContractPlans,
            utilityActivationItem,
            isInternetAmenityFlow,
          })}
        </span>
      </div>
      {isNoContractPlans && (
        <div styleName="subtitleBox">
          <span>
            It's safer to purchase internet plans that let
            <br />
            you cancel whenever you want.
          </span>
        </div>
      )}
      <div styleName="contentBox">
        {isInternetUtilityType && !utilityActivation.hasInternetAmenityEligibility && (
          <SrUtilitySetupTransferInternetButton onClick={onStartTransferInternetFlow} />
        )}
        {availableProviders.map((provider) => {
          // No need to sort plans anymore - just use them as they are
          const sortedPlans = provider.plans;

          return (
            <div styleName="providerBox" key={provider.id}>
              <div>
                {provider.logoUrl ? (
                  <SrBox styleName="providerLogo" backgroundImage={`url(${provider.logoUrl})`} />
                ) : (
                  <span styleName="providerNameText">{provider.title}</span>
                )}
              </div>
              {provider.marketingDescription && !utilityActivation.hasInternetAmenityEligibility && (
                <div styleName="marketingDescriptionBox">
                  <span>{provider.marketingDescription}</span>
                </div>
              )}
              <div styleName="chooseProviderPlanBox">
                <span>Choose a plan:</span>
              </div>
              <div styleName="providerPlansBox">
                {sortedPlans.map((plan) => {
                  const isDefaultInternetPlan =
                    defaultInternetAmenityPlan && defaultInternetAmenityPlan.id === plan.id;
                  const isInternetAmenityPlan = internetAmenityPlanIds.has(plan.id);

                  return (
                    <SrUtilityProfitProviderPlanCard
                      key={`${plan.id}-${plan.utilityProviderPlanVariantId || ''}`}
                      onClick={() => {
                        setUtilityProviderPlanId(plan.id);
                        setUtilityProviderPlanVariantId(plan.utilityProviderPlanVariantId || null);
                        setInternetAmenityPlanId(plan.internetAmenityPlanId || null);
                        setUtilityProviderId(provider.id);
                      }}
                      isActive={isSelectedPlan(plan)}
                      theme={
                        isInternetAmenityPlan &&
                        provider.purchaseFlowType === UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID
                          ? PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY
                          : plan.internetAmenityPlanId
                          ? PROVIDER_PLAN_CARD_THEME_AMENITY
                          : PROVIDER_PLAN_CARD_THEME_DEFAULT
                      }
                      utilityProviderPlan={plan}
                      internetPlanDescriptionV2={internetPlanDescriptionV2}
                      isDefaultInternetPlan={isDefaultInternetPlan}
                      utilityPartner={utilityPartner}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {showTransferedAccountModal && (
        <SrUtilitySetupTransferedAccountModal
          utilityActivation={utilityActivation}
          utilityActivationItem={utilityActivationItem}
          onClose={() => setShowTransferedAccountModal(false)}
        />
      )}
      {noContractPlanModalProps && (
        <SrUtilitySetupNonContractPlansModal
          {...noContractPlanModalProps}
          onSeeNoContractPlanOptions={onSeeNoContractPlanOptions}
          onContinue={onContinue}
          onClose={() => setNoContractPlanModalProps(null)}
        />
      )}
      {transferFlowIsActive && (
        <SrUtilitySetupTransferInternetFlow
          utilityActivationId={utilityActivation.id}
          utilityActivationItemId={utilityActivationItem.id}
          availableProviders={utilityActivationItem.availableProviders}
          onClose={onCloseTransferFlow}
          onShowTransferredAccountModal={() => {
            onCloseTransferFlow();
            setShowTransferedAccountModal(true);
          }}
          onSeeDeals={(providerId) => {
            const link = [
              '/utility-setup',
              utilityPartner.slug,
              utilityActivation.shortId,
              utilityActivationItem.type.label.toLowerCase(),
              'plan',
              providerId,
              'new-customer-deals',
            ].join('/');

            history.push(link);
          }}
          onSeeMatchingPlan={({ providerId, matchingPlanId }) => {
            const link = [
              '/utility-setup',
              utilityPartner.slug,
              utilityActivation.shortId,
              utilityActivationItem.type.label.toLowerCase(),
              'plan',
              providerId,
              `${matchingPlanId}?newCustomerDeals=true`,
            ].join('/');

            history.push(link);
          }}
          onUtilityActivationArchive={() => {
            return onRefreshUtilityActivation().then(() => {
              history.push(`/utility-setup/${utilityPartner.slug}/${utilityActivation.shortId}`);
            });
          }}
        />
      )}
      <SrStaticFooter size="130">
        <ButtonDeprecated
          type="submit"
          theme={BUTTON_THEME_GREEN_ADOBE}
          disabled={!utilityProviderPlanId}
          onClick={isInternetAmenityFlow ? onContinue : onCheckNonContractPlans}
        >
          {isInternetAmenityFlow ? 'Review & Ship Equipment' : 'Continue'}
        </ButtonDeprecated>
      </SrStaticFooter>
      {isFaqModalOpen && <UPResidualUtilityFaqModal onClose={() => setIsFaqModalOpen(false)} />}
    </div>
  );
};

SrUtilitySetupProviderPlan.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  utilityActivation: PropTypes.object.isRequired,
  utilityActivationItem: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  locationQuery: PropTypes.object.isRequired,
  isNoContractPlans: PropTypes.bool,
  transferFlowIsActive: PropTypes.bool,
  onCloseTransferFlow: PropTypes.func,
  onStartTransferInternetFlow: PropTypes.func,
  onRefreshUtilityActivation: PropTypes.func.isRequired,
  internetPlanDescriptionV2: PropTypes.string,
};

SrUtilitySetupProviderPlan.displayName = 'SrUtilitySetupProviderPlan';

export default SrUtilitySetupProviderPlan;
